import React, { useState } from "react";
import styled from "styled-components";
import DataTable from "../components/DataTable";
import Components from "../components";
import apiClient from "../utils/apiClient";
import { Helmet } from "react-helmet-async";
import AddIcon from "@mui/icons-material/Add";
import PageTitle from "../components/PageTitle";
import { Button, IconButton, Typography, Link, Box, Tab } from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import dayjs from "dayjs";
import VisibilityIcon from "@mui/icons-material/Visibility";
import StyledDrawer from "../components/StyledDrawer";
import AgentLegalForm from "../enum/AgentLegalForm.enum";
import { useTranslation } from "react-i18next";

const { CreateAgentForm } = Components;

const HeadTabMenu = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  gap: 10px;

  & > div:not(:last-child) {
    margin-inline-end: 10px;
  }
`;

const ButtonsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 0;
  position: relative;
  overflow: visible;
  gap: 10px;
`;

const AgentsAdmin = () => {
  const { t } = useTranslation();

  const [refreshCount, setRefreshCount] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null);
  const [listTab, setListTab] = useState("agents");

  const handleClosePopup = () => {
    setShowPopup(false);
    setEditingAgent(null);
    setEditingDraft(null);
  };

  const handleSuccessCreate = () => {
    setRefreshCount((prev) => prev + 1);
    handleClosePopup();
  };

  const handleDeleteDraft = async (draft) => {
    if (!draft?.id) {
      return;
    }

    if (!window.confirm(t("createAgent.formActions.deleteDraftConfirm"))) {
      return;
    }

    try {
      const response = await apiClient.delete(`/agent-drafts/${draft.id}`);

      if (response.data.status === "ok") {
        if (editingDraft?.id === draft.id) {
          handleClosePopup();
        }
        setRefreshCount((prev) => prev + 1);
        return;
      }

      alert(response.data?.error ?? t("createAgent.errors.deleteDraft"));
    } catch (error) {
      alert(
        error?.response?.data?.error ?? t("createAgent.errors.deleteDraft"),
      );
    }
  };

  const getLegalFormLabel = (value) => {
    switch (value) {
      case AgentLegalForm.PERSON:
        return t("agents.legalForm.person");
      case AgentLegalForm.ENTREPRENEUR:
        return t("agents.legalForm.entrepreneur");
      case AgentLegalForm.COMPANY:
        return t("agents.legalForm.company");
      default:
        return value;
    }
  };

  const agentColumns = [
    { field: "id", headerName: t("common.id"), flex: 0.5 },
    {
      field: "format",
      headerName: t("agents.columns.format"),
      flex: 0.5,
      valueFormatter: (value) => getLegalFormLabel(value),
    },
    { field: "fio", headerName: t("agents.columns.fio"), flex: 1.5 },
    {
      field: "email",
      headerName: t("common.email"),
      flex: 1,
      renderCell: (params) => (
        <Link href={`mailto:${params.value}`} title={params.value}>
          {params.value}
        </Link>
      ),
    },
    { field: "phone", headerName: t("agents.columns.phone"), flex: 1 },
    {
      field: "action",
      headerName: "",
      width: 180,
      type: "actions",
      renderCell: (params) => (
        <ButtonsBlock>
          {!params.row.is_o ? (
            <>
              <Typography color="textSecondary">
                {t("agents.closed")}{" "}
                {params.row?.data_end_doc
                  ? dayjs(params.row.data_end_doc).format("DD.MM.YYYY")
                  : ""}
              </Typography>
              <IconButton
                aria-label="open agent"
                onClick={() => {
                  setEditingAgent(params.row);
                  setEditingDraft(null);
                  setShowPopup(true);
                }}
                title={t("agents.openAgent")}
              >
                <VisibilityIcon />
              </IconButton>
            </>
          ) : (
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                setEditingAgent(params.row);
                setEditingDraft(null);
                setShowPopup(true);
              }}
            >
              {t("common.edit")}
            </Button>
          )}
        </ButtonsBlock>
      ),
    },
  ];

  const draftColumns = [
    { field: "id", headerName: t("common.id"), flex: 0.5 },
    {
      field: "format",
      headerName: t("agents.columns.format"),
      flex: 0.6,
      valueFormatter: (value) => getLegalFormLabel(value),
    },
    { field: "fio", headerName: t("agents.columns.fio"), flex: 1.5 },
    {
      field: "email",
      headerName: t("common.email"),
      flex: 1,
      renderCell: (params) => (
        <Link href={`mailto:${params.value}`} title={params.value}>
          {params.value}
        </Link>
      ),
    },
    { field: "phone", headerName: t("agents.columns.phone"), flex: 1 },
    {
      field: "action",
      headerName: "",
      width: 260,
      type: "actions",
      renderCell: (params) => (
        <ButtonsBlock>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => {
              setEditingDraft(params.row);
              setEditingAgent(null);
              setShowPopup(true);
            }}
          >
            {t("common.edit")}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => handleDeleteDraft(params.row)}
          >
            {t("common.delete")}
          </Button>
        </ButtonsBlock>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("agents.metaTitle")}</title>
        <meta name="description" content={t("agents.metaDescription")} />
      </Helmet>

      <PageTitle>{t("agents.title")}</PageTitle>

      <HeadTabMenu>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setEditingAgent(null);
            setEditingDraft(null);
            setShowPopup(true);
          }}
          fullWidth
          startIcon={<AddIcon />}
        >
          {listTab === "drafts" ? t("agents.addDraft") : t("agents.addAgent")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => setRefreshCount((prev) => prev + 1)}
          fullWidth
        >
          {t("common.refresh")}
        </Button>
      </HeadTabMenu>

      <TabContext value={listTab}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <TabList onChange={(_event, value) => setListTab(value)}>
            <Tab label={t("agents.tabs.agents")} value="agents" />
            <Tab label={t("agents.tabs.drafts")} value="drafts" />
          </TabList>
        </Box>

        <DataTable
          isShowSearch={true}
          columns={listTab === "drafts" ? draftColumns : agentColumns}
          path={listTab === "drafts" ? "/agent-drafts" : "/agents"}
          refresh={refreshCount}
        />
      </TabContext>

      <StyledDrawer
        show={showPopup}
        title={
          editingDraft
            ? `${t("agents.draft")} #${editingDraft?.id}`
            : editingAgent
              ? `${t("agents.agent")} ${editingAgent?.user_id_tn}`
              : listTab === "drafts"
                ? t("agents.addDraft")
                : t("agents.addAgent")
        }
        onClose={handleClosePopup}
        ContentComponent={CreateAgentForm}
        contentProps={{
          onClose: handleClosePopup,
          onSuccess: handleSuccessCreate,
          agent: editingAgent,
          draft: editingDraft,
        }}
        width="80vw"
      />
    </>
  );
};

export default AgentsAdmin;
